import type { BillingCommandErrorCode } from "./catalog";
import type { BillingRuntimeCheckpointV1 } from "./runtime";

export const BillingCommandStatusList = [
  "queued",
  "dispatching",
  "running",
  "waiting_on_shopify",
  "waiting_on_webhook",
  "blocked_manual_intervention",
  "pending_customer_action",
  "completed",
  "rejected",
  "failed_retryable",
  "failed_exhausted",
  "abandoned_external_approval",
  "recorded",
  "cancelled",
] as const;

export type BillingCommandStatus = (typeof BillingCommandStatusList)[number];

/**
 * A command in one of these statuses is still a live owner of whatever provider
 * resources it claimed: the runtime, or a human via command repair, may still
 * drive it to a charge. Acting on those resources behind its back strands it.
 *
 * `blocked_manual_intervention` belongs here. A Repair Verdict is not running,
 * but an operator can still resolve it, so its AppSubscription is not orphaned.
 * Dropping it from this list orphans a live subscription, and orphan
 * reconciliation on a Shopify-billed org lands another manual_followup — the
 * verdict regenerates itself.
 */
export const BILLING_COMMAND_LIVE_OWNER_STATUSES: readonly BillingCommandStatus[] =
  [
    "dispatching",
    "running",
    "waiting_on_shopify",
    "waiting_on_webhook",
    "blocked_manual_intervention",
    "pending_customer_action",
  ];

/**
 * A command in one of these statuses occupies its organization's Billing Lane:
 * it is the Lane Head, and every later command for that org waits behind it.
 *
 * Each member is awaiting a specific event that will arrive — the dispatcher,
 * the state machine, a provider webhook, or a merchant action. That is what
 * earns the right to hold the lane.
 *
 * `blocked_manual_intervention` is excluded. A Repair Verdict is a conclusion
 * about a past provider event, not an in-flight mutation, and no event resumes
 * it. Holding the lane behind one freezes the organization's billing forever
 * with nothing scheduled to release it. See billing-workflow ADR 0031.
 */
export const BILLING_COMMAND_LANE_BLOCKING_STATUSES: readonly BillingCommandStatus[] =
  [
    "dispatching",
    "running",
    "waiting_on_shopify",
    "waiting_on_webhook",
    "pending_customer_action",
  ];

/**
 * A command in one of these statuses is still in its organization's Billing
 * Lane queue: waiting to enter the lane (`queued`) or holding it. An org whose
 * commands are all outside this list has an idle lane — nothing is running and
 * nothing is owed a dispatch ahead of new work.
 */
export const BILLING_COMMAND_IN_LANE_STATUSES: readonly BillingCommandStatus[] =
  ["queued", ...BILLING_COMMAND_LANE_BLOCKING_STATUSES];

/**
 * Whether a command in this status has released its organization's Billing
 * Lane: every terminal status, plus a Repair Verdict — which keeps owning its
 * provider resources without holding the lane (ADR 0031). A `queued` command
 * has not released the lane; it is waiting to enter it.
 */
export function billingCommandStatusReleasesLane(
  status: BillingCommandStatus
): boolean {
  return !BILLING_COMMAND_IN_LANE_STATUSES.includes(status);
}

export const BILLING_COMMAND_TERMINAL_STATUSES: readonly BillingCommandStatus[] =
  [
    "completed",
    "rejected",
    "failed_retryable",
    "failed_exhausted",
    "abandoned_external_approval",
    "recorded",
    "cancelled",
  ];

export const BILLING_COMMAND_SETTLED_STATUSES: readonly BillingCommandStatus[] =
  [
    "completed",
    "rejected",
    "failed_exhausted",
    "abandoned_external_approval",
    "recorded",
    "cancelled",
  ];

/**
 * A settled command can never execute again. `failed_retryable` is terminal
 * for queue ordering and for its own row — its Step Functions execution name
 * is already consumed, so the attempt it names is over — but the work it
 * carries is not finished: the command-repair sweep owes it a successor,
 * minting a derived retry command that runs the same payload under a fresh id.
 * Anything deciding whether a command's external resources are safe to adopt
 * (e.g. a Shopify ownership row) must therefore use this predicate, never
 * BILLING_COMMAND_TERMINAL_STATUSES.
 */
export function isBillingCommandStatusSettled(
  status: BillingCommandStatus
): boolean {
  return BILLING_COMMAND_SETTLED_STATUSES.includes(status);
}

export const BillingCommandStatusSignalKindList = [
  "progress_checkpoint",
  "external_wait_started",
  "manual_block_started",
  "terminal_outcome",
] as const;

export type BillingCommandStatusSignalKind =
  (typeof BillingCommandStatusSignalKindList)[number];

export const BILLING_COMMAND_ALLOWED_STATUS_TRANSITIONS: Readonly<
  Record<BillingCommandStatus, readonly BillingCommandStatus[]>
> = {
  queued: ["dispatching", "failed_exhausted", "cancelled"],
  dispatching: [
    "running",
    "failed_retryable",
    "failed_exhausted",
    "blocked_manual_intervention",
    "cancelled",
  ],
  running: [
    "waiting_on_shopify",
    "waiting_on_webhook",
    "blocked_manual_intervention",
    "pending_customer_action",
    "completed",
    "rejected",
    "failed_retryable",
    "failed_exhausted",
    "abandoned_external_approval",
    "recorded",
    "cancelled",
  ],
  waiting_on_shopify: [
    "running",
    "blocked_manual_intervention",
    "pending_customer_action",
    "completed",
    "rejected",
    "failed_retryable",
    "failed_exhausted",
    "abandoned_external_approval",
    "cancelled",
  ],
  waiting_on_webhook: [
    "running",
    "blocked_manual_intervention",
    "pending_customer_action",
    "completed",
    "rejected",
    "failed_retryable",
    "failed_exhausted",
    "abandoned_external_approval",
    "cancelled",
  ],
  blocked_manual_intervention: [
    "running",
    "abandoned_external_approval",
    "failed_exhausted",
    "cancelled",
  ],
  pending_customer_action: [
    "completed",
    "rejected",
    "failed_retryable",
    "failed_exhausted",
    "abandoned_external_approval",
    "cancelled",
  ],
  completed: [],
  rejected: [],
  failed_retryable: ["running", "failed_exhausted", "cancelled"],
  failed_exhausted: [],
  abandoned_external_approval: [],
  recorded: [],
  cancelled: [],
};

export interface BillingCommandStatusSignal {
  commandId: string;
  orgId: string;
  commandStatus: Exclude<BillingCommandStatus, "queued" | "dispatching">;
  signalKind: BillingCommandStatusSignalKind;
  updatedAt: string;
  errorCode?: BillingCommandErrorCode;
  checkpoint?: BillingRuntimeCheckpointV1;
}
